
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {gatherPipeCandidates} from "./pipeCandidates.js";
import {
	orthonormalBasis,
	fitCircle2D,
	fitCylinderRANSAC,
	refineCylinderIRLS,
	distanceToAxis,
	paramAlongAxis,
	computeAngularCoverage,
	computeLongitudinalBins,
	voxelDownsample,
	verticalExtentOfCrossSection,
	closestPointOnAxis,
} from "./cylinderFit.js";

import {t} from "../i18n/index.js";
const QUALITY_RANK = {God: 2, Acceptabel: 1, Usikker: 0};

// The relResidual hard-gate threshold (see classifyQuality()) below which a
// fit is trusted by default. Exported so callers (pipeTool.js) can offer an
// explicit, user-confirmed relaxed retry using RELAXED_REL_RESIDUAL_THRESHOLD
// when the default rejects - never applied automatically/silently, and never
// exposed as a persistent Digitalisering setting (see settings/SettingsSchema.js's
// own doc comment on algorithm configuration vs. user preferences): a
// looser tolerance is a one-time, explicitly-informed choice per pipe, not a
// standing preference, since a user who forgets it's turned on would stop
// getting the safety the default threshold provides.
export const SAFE_REL_RESIDUAL_THRESHOLD = 0.15;
export const RELAXED_REL_RESIDUAL_THRESHOLD = 0.25;

// Orchestrates the full Pipe-tool pipeline (spec section 6/13/29):
//   2 direction points + 5 seeds
//     -> initial axis + initial circle from the seeds
//     -> gather real point-cloud candidates around the direction segment
//     -> density-normalize the fitting population (review §16)
//     -> multiple RANSAC-lite cylinder hypotheses (review §19/§22/§28)
//     -> each hypothesis independently IRLS-refined and hard-gated
//     -> the best hypothesis that actually passes every gate wins
//     -> Measurement Station + elevations (+ inner ones if wall thickness given)
//
// wallThicknessMeters is null when the user left "Godstykkelse" empty.
// Retningsstykket (de to retningspunkter) må højst være MAX_PIPE_DIRECTION_LENGTH meter langt - se computePipeResult().
// options.relResidualThreshold overrides SAFE_REL_RESIDUAL_THRESHOLD (see its
// doc comment) - only intended for an explicit, user-confirmed relaxed
// retry after an initial rejection, never a silent default.
// Returns either {status:'rejected', reason, code} or {status:'ok', quality,
// reducedAccuracy, ...}. `code` is a stable, machine-readable identifier for
// the rejection reason (the `reason` string is Danish/user-facing and may be
// reworded; match on `code`, not on `reason`, in calling code).
// Største tilladte afstand mellem de to retningspunkter (meter). Bruger-beslutning: 0,5 m.
export const MAX_PIPE_DIRECTION_LENGTH = 0.5;

// Det andet retningspunkt: ligger cursoren længere væk end MAX fra det første punkt, ender punktet på stregen fra det første
// punkt mod cursoren, præcis MAX væk (samme retning, kortere stykke) - så stregen i værktøjet aldrig bliver længere end grænsen.
// Returnerer ALTID en ny Vector3 (ændrer ikke argumenterne).
export function clampDirectionPoint(origin, point, maxLength = MAX_PIPE_DIRECTION_LENGTH){
	const offset = point.clone().sub(origin);
	const length = offset.length();
	if(length <= maxLength || length < 1e-12) return point.clone();
	return origin.clone().addScaledVector(offset, maxLength / length);
}

export function computePipeResult(pointclouds, directionA, directionB, seeds, wallThicknessMeters, options = {}){
	const relResidualThreshold = options.relResidualThreshold ?? SAFE_REL_RESIDUAL_THRESHOLD;

	const axisDir0 = directionB.clone().sub(directionA);
	const directionLength = axisDir0.length();
	if(directionLength < 1e-6){
		return {status: "rejected", code: "direction_points_too_close", reason: t("Retningspunkterne ligger for tæt på hinanden til at angive en rørretning.")};
	}
	// Maks. længde på retningsstykket (bruger-beslutning): et langt stykke (flere meter) gør analyseområdet så stort, at
	// kandidat-indsamlingen og tilpasningen løber løbsk. Værktøjet afviser allerede klikket, men også her, så intet kald - uanset
	// vej - kan sende et langt stykke videre til beregningen.
	if(directionLength > MAX_PIPE_DIRECTION_LENGTH + 1e-6){
		return {status: "rejected", code: "direction_too_long", reason: t("Retningsstykket er for langt ({0} m). Højst {1} m er tilladt - vælg det andet retningspunkt tættere på det første.", directionLength.toFixed(2).replace(".", ","), MAX_PIPE_DIRECTION_LENGTH.toString().replace(".", ","))};
	}
	axisDir0.normalize();

	// initial circle: project the 5 seeds onto the plane perpendicular to
	// the initial (rough) axis, through their own centroid
	const seedCentroid = average(seeds);
	const {u, v} = orthonormalBasis(axisDir0);
	const seeds2D = seeds.map((s) => {
		const d = s.clone().sub(seedCentroid);
		return {x: d.dot(u), y: d.dot(v)};
	});
	const initialCircle = fitCircle2D(seeds2D);
	if(!initialCircle || !(initialCircle.r > 0)){
		return {status: "rejected", code: "initial_circle_failed", reason: t("Kunne ikke beregne en initial cirkel ud fra de fem seed-punkter - prøv at sprede dem mere.")};
	}
	const initialCenter = seedCentroid.clone().addScaledVector(u, initialCircle.cx).addScaledVector(v, initialCircle.cy);

	// Clamp the seed-only Kåsa circle radius into a physically plausible
	// range before it drives anything downstream (search radius, RANSAC
	// seed, inlier threshold). The 5 seeds are placed on whatever arc of the
	// pipe is visible from the current camera angle, which from many real
	// viewpoints (e.g. looking down into a trench) covers only a narrow,
	// close-to-collinear slice - Kåsa's circle fit is degenerate for
	// near-collinear points and can report anything from a few millimeters
	// to well over a meter for a real pipe that's a few centimeters across.
	// Both directions have been seen against real point-cloud data: an
	// under-estimate causes false rejections (compared to a good fit later),
	// an over-estimate inflates searchRadius enough that RANSAC/IRLS can
	// lock onto a large competing surface (e.g. a trench wall) and still
	// pass a search-region-relative sanity check because that check scales
	// with the same bad estimate. Utility pipes in this tool's intended use
	// case are assumed to fall within roughly 2cm-20cm radius (4cm-40cm
	// diameter) - a 0.5m ceiling tried earlier still let searchRadius grow
	// large enough (up to ~0.7m) to swallow an entire trench cross-section
	// as "candidates" and fit a big enclosing cylinder to it (confirmed
	// against real data: 360 degree coverage, 60k+ inliers, ~0.85m
	// diameter - clearly the trench, not the pipe). Clamping here keeps
	// every downstream calculation anchored to the intended pipe-size
	// assumption regardless of how the raw seed geometry came out.
	const initialRadius = Math.min(Math.max(initialCircle.r, 0.02), 0.2);

	// gather real candidate points around the direction segment - search
	// radius comfortably larger than the rough estimate so the true surface
	// (which the 5 seeds only approximate) is still captured, but not so
	// generous that it reaches a competing curved surface right next to the
	// pipe (e.g. a trench/excavation wall a few tens of cm away) - a bigger,
	// denser competing surface can win the RANSAC inlier-count contest
	// outright if it's included at all. 1.4x was tightened down from an
	// initial 1.8x after exactly this failure mode showed up against real
	// point-cloud data (a pipe sitting close to a curved dirt wall).
	const searchRadius = Math.max(initialRadius * 1.4, 0.12);
	const candidates = gatherPipeCandidates(pointclouds, directionA, directionB, searchRadius);
	if(candidates.length < 20){
		return {status: "rejected", code: "insufficient_candidates", reason: t("For få punktskypunkter fundet omkring det udpegede rør til en sikker beregning.")};
	}

	// Density normalization (review §16): downsample the candidate
	// population onto a uniform voxel grid before it drives RANSAC
	// sampling/scoring and IRLS refinement, so a denser nearby surface (e.g.
	// a trench wall scanned at higher point density than the pipe itself)
	// can't win purely by raw point volume regardless of which surface is
	// geometrically the better cylinder. Final QA below (residuals,
	// coverage, seed checks) still uses the full-resolution `candidates`, so
	// support/confidence numbers reflect the real data, not the downsampled
	// proxy. Voxel size is tied to the pipe's own scale (5% of the initial
	// radius estimate) rather than a fixed value, so it stays fine-grained
	// for a small pipe and doesn't over-thin a large one.
	const voxelSize = Math.max(initialRadius * 0.05, 0.002);
	const fittingPopulation = voxelDownsample(candidates, voxelSize);
	if(fittingPopulation.length < 20){
		return {status: "rejected", code: "insufficient_candidates", reason: t("For få punktskypunkter fundet omkring det udpegede rør til en sikker beregning.")};
	}

	const inlierThreshold = Math.max(initialRadius * 0.06, 0.01);

	// Computed once from the shared seed-based initialRadius, NOT from any
	// individual hypothesis's own fitted radius - deliberately, so every
	// hypothesis is judged against a neighborhood of the same physical size.
	// A per-hypothesis radius-scaled neighborhood was tried first and
	// discarded: it mechanically favored larger-radius hypotheses (a bigger
	// claimed radius -> a bigger neighborhood -> more raw points -> an
	// easier time clearing an absolute supporting-point-count bar) even when
	// the larger hypothesis was not actually a better fit to the seeds -
	// silently reintroducing exactly the "bigger cylinder wins" bias the
	// rest of this pipeline is built to avoid (review §25/§30).
	const seedNeighborhoodRadius = Math.max(initialRadius * 0.5, 0.03);

	// NOTE (iterative seed-neighborhood refinement): seedRefinement.js
	// implements a robust, anchored, iterative refinement of the initial
	// seed-based guess (see its own doc comment), built and benchmarked as a
	// candidate improvement to the starting point handed to RANSAC below.
	// Controlled A/B tests (see seedRefinement.test.js), matching the RANSAC
	// random-sampling sequence between arms for a fair comparison, showed the
	// existing RANSAC+IRLS pipeline is already robust enough to typical seed
	// imprecision (including one badly-placed seed, or nearby ground
	// clutter) that a better starting point produces materially identical
	// final results in most tested scenarios, and small/mixed (not
	// unambiguously better) differences in the rest - never a clear,
	// regression-free win. Deliberately NOT wired in here for that reason;
	// the module stays available/tested for a future attempt - e.g. if it
	// were also used to refine searchRadius/inlierThreshold/voxelSize rather
	// than only the RANSAC starting point.

	// Multi-hypothesis RANSAC (review §19/§22/§28): keep the best 5 random-
	// subset fits by inlier count, not just the single winner. Each is
	// independently IRLS-refined and run through the same hard gates below
	// (seed consistency, radius bounds, longitudinal support, radius
	// stability, coverage consistency); the winner is whichever hypothesis
	// actually PASSES those gates with the best quality, not whichever had
	// the most raw inliers going in - a competing surface's random sample
	// can occasionally out-score the real pipe's on inlier count alone.
	const hypotheses = fitCylinderRANSAC(fittingPopulation, initialCenter, axisDir0, initialRadius, {inlierThreshold, keepTopK: 5});
	if(!hypotheses || hypotheses.length === 0){
		return {status: "rejected", code: "no_cylinder_fit", reason: t("Kunne ikke tilpasse en cylinder til punktskydataene omkring røret.")};
	}

	const evalContext = {fittingPopulation, candidates, inlierThreshold, seedNeighborhoodRadius, axisDir0, seeds, directionA, directionB, wallThicknessMeters, searchRadius, relResidualThreshold};

	let best = null;
	let firstRejection = null;
	for(const hypothesis of hypotheses){
		const evaluated = evaluateHypothesis(hypothesis, evalContext);
		if(evaluated.status === "rejected"){
			if(!firstRejection) firstRejection = evaluated;
			continue;
		}
		if(!best || isBetterResult(evaluated, best)) best = evaluated;
	}

	return best || firstRejection || {status: "rejected", code: "no_cylinder_fit", reason: t("Kunne ikke tilpasse en cylinder til punktskydataene omkring røret.")};
}

// Quality tier first (God > Acceptabel > Usikker), then lower relative
// residual as a tiebreak within the same tier.
function isBetterResult(a, b){
	const rankA = QUALITY_RANK[a.quality] ?? 0;
	const rankB = QUALITY_RANK[b.quality] ?? 0;
	if(rankA !== rankB) return rankA > rankB;
	return a.relResidual < b.relResidual;
}

// Runs one RANSAC hypothesis through IRLS refinement and every hard gate,
// returning either {status:'rejected', reason} or a full {status:'ok', ...}
// result - see computePipeResult() for how the best of several hypotheses
// is chosen once all of them have been evaluated this way.
function evaluateHypothesis(hypothesis, ctx){
	const {fittingPopulation, candidates, inlierThreshold, seedNeighborhoodRadius, axisDir0, seeds, directionA, directionB, wallThicknessMeters, searchRadius, relResidualThreshold} = ctx;

	const inliers = filterInliers(fittingPopulation, hypothesis.fit, inlierThreshold);
	if(inliers.length < 10){
		return {status: "rejected", code: "insufficient_inliers", reason: t("For få punkter passer til en cylinderoverflade til en sikker dimensionsbestemmelse.")};
	}

	// axisDirReference: axisDir0, not hypothesis.fit.axisDir - anchors total
	// drift across RANSAC+IRLS together to the user's own two direction
	// points, not just this stage's own starting point (see cylinderFit.js)
	const refined = refineCylinderIRLS(inliers, hypothesis.fit.axisPoint, hypothesis.fit.axisDir, hypothesis.fit.radius, {axisDirReference: axisDir0});
	if(!refined || !(refined.radius > 0)){
		return {status: "rejected", code: "fit_did_not_converge", reason: t("Den robuste tilpasning konvergerede ikke til en gyldig cylinder.")};
	}

	// NOTE (surface-normal axis estimation): cylinderFit.js's
	// estimateAxisFromNormals()/estimateLocalNormals() were built and
	// benchmarked as a candidate replacement/correction for the slice-based
	// axis refinement above, but controlled synthetic A/B tests (see
	// cylinderFit.test.js) showed the local-PCA-derived normal estimate is
	// *consistently noisier* than the existing slice-and-line-fit approach
	// at the point counts this tool actually works with (low hundreds to a
	// few thousand candidates) - it only closes the gap in the thousands-of-
	// points regime, and is far worse than the existing method on narrow-arc/
	// short-segment cases specifically (the cases it was meant to help).
	// Deliberately NOT wired into the pipeline: doing so would occasionally
	// regress axis accuracy versus the current pipeline (still be visible in
	// the RESULT, since hard gates would still reject a badly wrong axis
	// but not gently-wrong ones from a false confidence pass). The functions
	// stay available/tested for a future attempt - e.g. driven by real
	// per-point normals from the point cloud itself instead of a sparse-
	// candidate-derived local PCA, if such an attribute is ever available.

	// Seed *region* consistency (review §6/§12-14/§27/§40): a seed click
	// means "the pipe surface is near here", not "this exact XYZ must lie on
	// the cylinder" - the raw ray-cast hit can land on an isolated noise
	// point, a bit of debris, or (confirmed against real data) occasionally
	// miss the pipe entirely and hit something far behind it. Checking the
	// single clicked point's own residual (the previous approach) means one
	// bad ray-cast can reject an otherwise perfectly good registration
	// outright. Instead, gather a small neighborhood of real candidate
	// points around each seed and check whether *that neighborhood*, in
	// aggregate, supports the fitted cylinder - robust to exactly the kind
	// of single-point miss seen in testing, while a seed that's genuinely
	// nowhere near the analyzed surface still fails immediately: its
	// neighborhood comes up empty, because `candidates` was only ever
	// gathered within `searchRadius` of the axis, and a wildly-off seed sits
	// well outside that region.
	//
	// This is a hard gate, not folded into the quality score, precisely
	// because a bad fit can otherwise look good on every other metric - the
	// fit is computed from the same (possibly wrong) candidate set it then
	// gets scored against, so seed-region support is the one signal that
	// doesn't share that blind spot.
	const seedRegions = seeds.map((s) => classifySeedRegion(s, candidates, refined, inlierThreshold, seedNeighborhoodRadius));
	const supportingCount = seedRegions.filter((r) => r.level === "Supporting").length;
	const inconsistentCount = seedRegions.filter((r) => r.level === "Inconsistent").length;
	if(supportingCount < 4 || inconsistentCount > 0){
		return {status: "rejected", code: "seed_mismatch", reason: t("Den beregnede cylinder passer ikke til de fem udpegede seed-punkters lokale overflade - resultatet ser ud til at være låst til en anden overflade end den, du pegede på. Prøv igen med seed-punkter tættere på rørets synlige overflade.")};
	}

	// Sanity check against the search region itself, not against the seeds'
	// own (Kåsa-fit) initial radius - that first estimate is only as good as
	// the angular spread of 5 user clicks, and from many real camera angles
	// (e.g. looking down into a trench from above) the visible arc of a pipe
	// is narrow enough that the 5 seeds end up nearly collinear in the plane
	// perpendicular to the axis. Kåsa's circle fit is degenerate for
	// near-collinear points and can report a radius of a few millimeters for
	// a real pipe that's several centimeters across - comparing against that
	// number produces false rejections on perfectly good fits.
	//
	// The actual signature of "RANSAC locked onto a competing surface" (e.g.
	// a trench wall a short distance from the real pipe) is a fitted radius
	// that approaches or exceeds the search region itself: candidates were
	// only gathered within `searchRadius` of the axis, so a genuine pipe's
	// radius must be comfortably smaller than that region, whereas a
	// larger/flatter competing surface will barely curve within such a small
	// window and can fit with an implausibly large radius.
	if(refined.radius > searchRadius * 1.5){
		return {status: "rejected", code: "radius_exceeds_search_region", reason: t("Den beregnede cylinder er for stor i forhold til søgeområdet omkring røret - punktskyen omkring seed-punkterne ligner ikke et rør her.")};
	}
	// absolute backstop, independent of any seed-derived estimate: nothing
	// in this workflow's intended use case is a >0.25m-radius pipe.
	if(refined.radius > 0.25 || refined.radius < 0.005){
		return {status: "rejected", code: "radius_out_of_range", reason: t("Den beregnede rørradius er urealistisk - kontrollér at retnings- og seed-punkterne er placeret korrekt på røret.")};
	}

	const finalInliers = filterInliers(candidates, refined, inlierThreshold);
	if(finalInliers.length < 10){
		return {status: "rejected", code: "insufficient_final_inliers", reason: t("For få punkter passer til den endelige cylinder til en sikker dimensionsbestemmelse.")};
	}

	const coverage = computeAngularCoverage(finalInliers, refined.axisPoint, refined.axisDir);

	const residuals = finalInliers.map((p) => distanceToAxis(p, refined.axisPoint, refined.axisDir) - refined.radius);
	const rms = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / residuals.length);
	const relResidual = rms / refined.radius;

	// Robust (MAD-based) estimate of this specific point cloud's own surface
	// noise, derived from the final fit's own residuals rather than judged
	// against a flat fraction of radius (review §7/§20/§37). A real,
	// well-supported fit on a rough/noisy scan (visible surface
	// debris/texture) can legitimately have a higher raw residual than one
	// on a clean synthetic cloud - comparing the residual against the noise
	// actually observed in *this* dataset, instead of one fixed threshold
	// for every dataset, avoids misclassifying a correct fit as "Usikker"
	// just because the source scan happens to be a bit rough.
	const medianResidual = median(residuals);
	const mad = median(residuals.map((r) => Math.abs(r - medianResidual)));
	const sigmaSurface = Math.max(1.4826 * mad, refined.radius * 0.002);
	const residualToNoiseRatio = rms / sigmaSurface;

	const radiusHistory = refined.radiusHistory || [refined.radius];
	const radiusVariation = radiusHistory.length > 1 ? stdev(radiusHistory) / mean(radiusHistory) : 0;

	const ts = finalInliers.map((p) => paramAlongAxis(p, refined.axisPoint, refined.axisDir));
	const sectionLength = Math.max(...ts) - Math.min(...ts);

	// Longitudinal bins (review §33/§34/§36/§39/§40/§43): slice the final
	// inliers into 6 bins along the axis and check three independent things
	// a single aggregate number can hide - see computeLongitudinalBins()'s
	// own doc comment for why each of these three checks exists.
	const longitudinal = computeLongitudinalBins(finalInliers, refined.axisPoint, refined.axisDir, 6);
	const minBinPoints = Math.max(5, Math.floor(finalInliers.length * 0.02));
	const validBins = longitudinal.bins.filter((b) => b.count >= minBinPoints);

	if(validBins.length < Math.ceil(longitudinal.bins.length * 0.5)){
		return {status: "rejected", code: "insufficient_longitudinal_support", reason: t("De punkter, der understøtter cylinderen, er koncentreret i et for kort stykke af det analyserede område til en sikker rørmåling. Prøv med retningspunkter, der spænder over en større del af det synlige rør.")};
	}

	const binRadii = validBins.map((b) => b.radius).filter((r) => r != null);
	let radiusSpatialVariation = 0;
	if(binRadii.length >= 3){
		const medianBinRadius = median(binRadii);
		const madBinRadius = median(binRadii.map((r) => Math.abs(r - medianBinRadius)));
		radiusSpatialVariation = medianBinRadius > 0 ? madBinRadius / medianBinRadius : 0;
		if(radiusSpatialVariation > 0.35){
			return {status: "rejected", code: "radius_unstable_along_length", reason: t("Rørets radius varierer for meget langs det analyserede stykke til at være et ægte, jævnt rør her - punktskyen ligner ikke en ensartet cylinder.")};
		}
	}

	const coverageBins = validBins.filter((b) => b.count >= 15);
	if(coverageBins.length >= 3){
		const minBinCoverage = Math.min(...coverageBins.map((b) => b.coverageDeg));
		if(minBinCoverage < Math.min(30, coverage.degrees * 0.3)){
			return {status: "rejected", code: "coverage_inconsistent", reason: t("Den synlige rørkrumning er ikke konsistent langs det analyserede stykke - den samlede dækning ser ud til at stamme fra forskellige overflader forskellige steder langs røret.")};
		}
	}

	const qc = classifyQuality({
		coverageDeg: coverage.degrees,
		relResidual,
		residualToNoiseRatio,
		inlierCount: finalInliers.length,
		radiusVariation,
		relResidualThreshold,
	});
	if(qc.rejected){
		return {status: "rejected", code: qc.code, reason: qc.reason};
	}

	// Measurement Station (spec §20): midpoint of the two direction points,
	// projected onto the *finished* axis
	const directionMid = directionA.clone().add(directionB).multiplyScalar(0.5);
	const station = closestPointOnAxis(directionMid, refined.axisPoint, refined.axisDir);

	const outer = verticalExtentOfCrossSection(station, refined.axisDir, refined.radius);

	let inner = null;
	if(wallThicknessMeters != null){
		const innerRadius = refined.radius - wallThicknessMeters;
		if(!(innerRadius > 0)){
			return {status: "rejected", code: "wall_thickness_invalid", reason: t("Den angivne godstykkelse er større end eller lig den beregnede udvendige radius.")};
		}
		const innerExtent = verticalExtentOfCrossSection(station, refined.axisDir, innerRadius);
		inner = {radius: innerRadius, topZ: innerExtent.topZ, bottomZ: innerExtent.bottomZ};
	}

	return {
		status: "ok",
		quality: qc.level,
		// true only when this fit would have been rejected at the standard
		// safe threshold (SAFE_REL_RESIDUAL_THRESHOLD) and only passed because
		// a caller explicitly requested a relaxed relResidualThreshold - see
		// that constant's doc comment. Computed from the actual residual, not
		// from whether a relaxed threshold was merely *passed in*, so this is
		// always false on a normal (default-threshold) call by construction.
		reducedAccuracy: relResidual > SAFE_REL_RESIDUAL_THRESHOLD,
		axisPoint: refined.axisPoint,
		axisDir: refined.axisDir,
		radius: refined.radius,
		wallThicknessMeters: wallThicknessMeters,
		innerRadius: inner ? inner.radius : null,
		station,
		centerZ: station.z,
		outerTopZ: outer.topZ,
		outerBottomZ: outer.bottomZ,
		innerTopZ: inner ? inner.topZ : null,
		innerBottomZ: inner ? inner.bottomZ : null,
		sectionLength,
		angularCoverageDeg: coverage.degrees,
		inlierCount: finalInliers.length,
		inlierPoints: finalInliers,
		relResidual,
		// QA diagnostics (review §48/§57) - not shown in the label, but kept
		// on the feature for debugging/audit and future quality-tuning work
		seedRegions,
		sigmaSurface,
		residualToNoiseRatio,
		radiusSpatialVariation,
		longitudinalBinsCovered: validBins.length,
		longitudinalBinCount: longitudinal.bins.length,
		ransacInlierCount: hypothesis.inlierCount,
	};
}

function filterInliers(candidates, fit, threshold){
	return candidates.filter((p) => Math.abs(distanceToAxis(p, fit.axisPoint, fit.axisDir) - fit.radius) <= threshold);
}

// Classifies one seed *region* (a local neighborhood of real candidate
// points around the raw click, not the click itself) against the finished
// cylinder fit - see the seed-region-consistency comment in
// evaluateHypothesis() for why this replaced checking the exact clicked
// point. `candidates` is the full-resolution point population already
// gathered near the direction segment (not the voxel-downsampled fitting
// population), so support/confidence here reflects the real data.
//
// - "Inconsistent": too few real points near the seed to judge (including
//   none at all - the seed-click landed outside the analyzed region
//   entirely, which is exactly the "wildly missed the pipe" failure mode
//   this is meant to catch), or barely any of them sit on the fitted
//   surface.
// - "Weak": some real support, but not enough to be confident.
// - "Supporting": a solid absolute number of the local neighborhood's
//   points lie on the fitted cylinder surface within the normal inlier
//   tolerance.
//
// Deliberately graded on absolute supporting-point count, not the fraction
// of the neighborhood that supports the fit (tried first; recalibrated
// after real testing). A seed placed near a genuine object boundary (e.g.
// the pipe/soil transition) has a real, physically mixed local
// neighborhood - roughly half soil, half pipe is completely normal there,
// not a sign of a bad click. Confirmed against real data: a seed with a
// clearly-correct, well-supported cylinder fit had a neighborhood that was
// "only" 37-38% supporting by fraction (a 552-892-point neighborhood, so
// ~200-300 real supporting points) - a fraction-based cutoff at 50% would
// have wrongly flagged that as failing. What actually distinguishes a good
// seed from a bad one is whether real pipe-surface points exist nearby in
// meaningful numbers, not what fraction of an arbitrarily-sized ball they
// make up.
function classifySeedRegion(seed, candidates, fit, inlierThreshold, neighborhoodRadius){
	const neighborhood = candidates.filter((p) => p.distanceTo(seed) <= neighborhoodRadius);
	if(neighborhood.length < 5){
		return {level: "Inconsistent", supportFraction: 0, supportingCount: 0, neighborhoodCount: neighborhood.length};
	}

	const supportingCount = neighborhood.filter((p) => Math.abs(distanceToAxis(p, fit.axisPoint, fit.axisDir) - fit.radius) <= inlierThreshold).length;
	const supportFraction = supportingCount / neighborhood.length;

	let level;
	if(supportingCount >= 20 && supportFraction >= 0.15) level = "Supporting";
	else if(supportingCount >= 5) level = "Weak";
	else level = "Inconsistent";

	return {level, supportFraction, supportingCount, neighborhoodCount: neighborhood.length};
}

function average(points){
	const sum = new THREE.Vector3();
	for(const p of points) sum.add(p);
	return sum.divideScalar(points.length);
}

function mean(values){
	return values.reduce((a, b) => a + b, 0) / values.length;
}

function stdev(values){
	const m = mean(values);
	const variance = values.reduce((s, v) => s + (v - m) * (v - m), 0) / values.length;
	return Math.sqrt(variance);
}

function median(values){
	const sorted = [...values].sort((a, b) => a - b);
	const n = sorted.length;
	if(n === 0) return 0;
	return n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
}

// NOTE: these thresholds are reasonable engineering defaults chosen to make
// the quality gate actually discriminating, not values empirically
// validated against real PointView datasets - per spec §17 they should be
// tuned after real-world testing. Kept in one place so that's easy to do
// later without touching the rest of the pipeline.
//
// relResidual and residualToNoiseRatio are both checked, not just one:
// relResidual (residual as a fraction of the pipe's own radius) catches a
// genuinely bad fit regardless of how noisy the source scan is - nothing
// should be classified "God" at a large fraction of its own radius no
// matter how rough the data is. residualToNoiseRatio (residual relative to
// this specific point cloud's own measured noise floor, see sigmaSurface in
// evaluateHypothesis()) is what actually adapts to the dataset: it stops a
// correct fit on a rough/noisy scan from being wrongly downgraded just
// because a flat threshold didn't account for that noise. The relResidual
// cutoffs were loosened slightly from the original 0.04/0.08 flat
// thresholds precisely because residualToNoiseRatio is now doing the
// adaptive discrimination that relResidual alone used to be responsible for.
function classifyQuality({coverageDeg, relResidual, residualToNoiseRatio, inlierCount, radiusVariation, relResidualThreshold}){
	if(coverageDeg < 40 || inlierCount < 10){
		return {rejected: true, code: "insufficient_coverage", reason: t("Utilstrækkelig synlig røroverflade til sikker dimensionsbestemmelse.")};
	}
	if(relResidual > relResidualThreshold){
		return {rejected: true, code: "fit_quality_too_low", reason: t("Punktskydata omkring røret passer for dårligt til en cylinder til et sikkert resultat.")};
	}

	if(coverageDeg >= 140 && relResidual < 0.06 && residualToNoiseRatio < 2.2 && inlierCount >= 40 && radiusVariation < 0.03){
		return {rejected: false, level: t("God")};
	}
	if(coverageDeg >= 70 && relResidual < 0.10 && residualToNoiseRatio < 4 && inlierCount >= 15){
		return {rejected: false, level: t("Acceptabel")};
	}
	return {rejected: false, level: t("Usikker")};
}
